(function () {
  class SimplexNoise {
    constructor() {
      this.grad3 = [
        [1, 1],
        [-1, 1],
        [1, -1],
        [-1, -1],
        [1, 0],
        [-1, 0],
        [1, 0],
        [-1, 0],
        [0, 1],
        [0, -1],
        [0, 1],
        [0, -1],
      ];

      const source = new Uint8Array(256);
      for (let i = 0; i < 256; i += 1) source[i] = i;

      let seed = 1337;
      for (let i = 255; i > 0; i -= 1) {
        seed = (seed * 1664525 + 1013904223) >>> 0;
        const r = seed % (i + 1);
        const value = source[i];
        source[i] = source[r];
        source[r] = value;
      }

      this.perm = new Uint8Array(512);
      for (let i = 0; i < 512; i += 1) this.perm[i] = source[i & 255];
    }

    dot(grad, x, y) {
      return grad[0] * x + grad[1] * y;
    }

    noise2D(xin, yin) {
      const f2 = 0.5 * (Math.sqrt(3) - 1);
      const g2 = (3 - Math.sqrt(3)) / 6;
      const s = (xin + yin) * f2;
      const i = Math.floor(xin + s);
      const j = Math.floor(yin + s);
      const t = (i + j) * g2;
      const x0 = xin - (i - t);
      const y0 = yin - (j - t);
      const i1 = x0 > y0 ? 1 : 0;
      const j1 = x0 > y0 ? 0 : 1;
      const x1 = x0 - i1 + g2;
      const y1 = y0 - j1 + g2;
      const x2 = x0 - 1 + 2 * g2;
      const y2 = y0 - 1 + 2 * g2;
      const ii = i & 255;
      const jj = j & 255;
      const gi0 = this.perm[ii + this.perm[jj]] % 12;
      const gi1 = this.perm[ii + i1 + this.perm[jj + j1]] % 12;
      const gi2 = this.perm[ii + 1 + this.perm[jj + 1]] % 12;
      let n0 = 0;
      let n1 = 0;
      let n2 = 0;

      let tt = 0.5 - x0 * x0 - y0 * y0;
      if (tt > 0) {
        tt *= tt;
        n0 = tt * tt * this.dot(this.grad3[gi0], x0, y0);
      }

      tt = 0.5 - x1 * x1 - y1 * y1;
      if (tt > 0) {
        tt *= tt;
        n1 = tt * tt * this.dot(this.grad3[gi1], x1, y1);
      }

      tt = 0.5 - x2 * x2 - y2 * y2;
      if (tt > 0) {
        tt *= tt;
        n2 = tt * tt * this.dot(this.grad3[gi2], x2, y2);
      }

      return 70 * (n0 + n1 + n2);
    }
  }

  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const smoothstep = (value) => {
    const t = clamp(value, 0, 1);
    return t * t * (3 - 2 * t);
  };

  function readColor(value, fallback) {
    const color = String(value || fallback || '#ffffff').trim();
    const hex = color.match(/^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
    if (!hex) return readColor(fallback || '#ffffff', '#ffffff');

    return {
      r: parseInt(hex[1], 16),
      g: parseInt(hex[2], 16),
      b: parseInt(hex[3], 16),
    };
  }

  function mix(a, b, amount) {
    const t = clamp(amount, 0, 1);
    return {
      r: a.r + (b.r - a.r) * t,
      g: a.g + (b.g - a.g) * t,
      b: a.b + (b.b - a.b) * t,
    };
  }

  function numberFromData(element, name, fallback) {
    const value = Number(element.dataset[name]);
    return Number.isFinite(value) ? value : fallback;
  }

  function organicFluidValue(simplex, x, y, time, scale, mouse) {
    const px = x / scale;
    const py = y / scale;
    const mouseStrength = mouse.influence * mouse.influence;
    const mouseDx = mouse.area > 0 ? (x - mouse.x) / mouse.area : 0;
    const mouseDy = mouse.area > 0 ? (y - mouse.y) / mouse.area : 0;
    const swirl = mouse.active ? Math.atan2(mouseDy, mouseDx) : 0;
    const mouseWarpX = Math.cos(swirl + time * 0.65) * mouseStrength * 0.72 - mouseDy * mouseStrength * 0.38;
    const mouseWarpY = Math.sin(swirl - time * 0.53) * mouseStrength * 0.72 + mouseDx * mouseStrength * 0.38;

    const warpA = simplex.noise2D(px * 0.62 + Math.sin(time * 0.37) * 0.8, py * 0.62 + Math.cos(time * 0.29) * 0.8);
    const warpB = simplex.noise2D(px * 0.58 + 14.7 + Math.cos(time * 0.23) * 0.9, py * 0.58 - 9.3 + Math.sin(time * 0.31) * 0.9);
    const warpC = simplex.noise2D(px * 0.32 - 4.1 + Math.sin(time * 0.17), py * 0.32 + 8.8 + Math.cos(time * 0.19));
    const warpAmount = 1.12 + warpC * 0.36;
    const wx = px + warpA * warpAmount + mouseWarpX;
    const wy = py + warpB * warpAmount + mouseWarpY;

    const large = simplex.noise2D(
      wx * 0.92 + Math.sin(time * 0.11) * 1.35 + warpB * 0.18,
      wy * 0.92 + Math.cos(time * 0.13) * 1.35 + warpA * 0.18
    );
    const medium = simplex.noise2D(
      wx * 1.82 + Math.cos(time * 0.27 + 2.1) * 0.86 - warpA * 0.24,
      wy * 1.82 + Math.sin(time * 0.21 - 1.4) * 0.86 + warpB * 0.24
    );
    const detail = simplex.noise2D(
      wx * 4.4 + Math.sin(time * 0.49 + large) * 0.42,
      wy * 4.4 + Math.cos(time * 0.43 + medium) * 0.42
    );

    const blended = large * 0.62 + medium * 0.28 + detail * 0.1;
    const softened = smoothstep((blended + 1) * 0.5);
    const edgeLife = (medium * 0.5 + detail * 0.5) * 0.09;

    return clamp(softened + edgeLife + mouseStrength * 0.08, 0, 1);
  }

  function initGlobalBackgroundNoise() {
    const canvas = document.querySelector('[data-global-background-noise]');
    if (!canvas || canvas.dataset.noiseReady === 'true') return;

    const ctx = canvas.getContext('2d', { alpha: false });
    if (!ctx) return;

    canvas.dataset.noiseReady = 'true';

    const simplex = new SimplexNoise();
    const mouse = {
      x: window.innerWidth / 2,
      y: window.innerHeight / 2,
      tx: window.innerWidth / 2,
      ty: window.innerHeight / 2,
      active: false,
    };

    const state = {
      width: 0,
      height: 0,
      ratio: 0.38,
      image: null,
      frameId: null,
      running: false,
      lastDraw: 0,
      reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    };

    function resize() {
      const width = window.innerWidth || document.documentElement.clientWidth || 1;
      const height = window.innerHeight || document.documentElement.clientHeight || 1;
      const ratio = Math.min(width < 750 ? 0.45 : 0.35, 420 / width);
      state.width = Math.max(1, Math.round(width * ratio));
      state.height = Math.max(1, Math.round(height * ratio));
      state.ratio = ratio;
      canvas.width = state.width;
      canvas.height = state.height;
      state.image = ctx.createImageData(state.width, state.height);
    }

    function scheduleDraw() {
      state.frameId = window.requestAnimationFrame(draw);
    }

    function stop() {
      if (state.frameId) window.cancelAnimationFrame(state.frameId);
      state.frameId = null;
      state.running = false;
    }

    function start() {
      if (state.running) return;
      state.running = true;
      scheduleDraw();
    }

    function draw(timestamp) {
      const body = document.body;
      if (!body.classList.contains('global-background-mode-noise')) {
        stop();
        return;
      }

      if (!state.image) resize();

      const frameInterval = state.reducedMotion ? 160 : 42;
      if (timestamp - state.lastDraw < frameInterval) {
        scheduleDraw();
        return;
      }
      state.lastDraw = timestamp;

      const styles = window.getComputedStyle(document.documentElement);
      const bg = readColor(styles.getPropertyValue('--global-noise-bg-color'), '#f8f6ff');
      const hover = readColor(styles.getPropertyValue('--global-noise-hover-color'), '#ffd6f3');
      const color1 = readColor(styles.getPropertyValue('--global-noise-color-1'), '#ff9edb');
      const color2 = readColor(styles.getPropertyValue('--global-noise-color-2'), '#9edbff');
      const opacity = clamp(numberFromData(canvas, 'opacity', 0.35), 0, 1);
      const speed = numberFromData(canvas, 'speed', 15);
      const scale = Math.max(40, numberFromData(canvas, 'scale', 220));
      const mouseArea = Math.max(0, numberFromData(canvas, 'mouseArea', 400));
      const mouseEase = clamp(numberFromData(canvas, 'mouseEase', 0.08), 0.01, 1);
      const time = timestamp * speed * 0.000018;
      const data = state.image.data;
      const invRatio = 1 / state.ratio;

      mouse.x += (mouse.tx - mouse.x) * mouseEase;
      mouse.y += (mouse.ty - mouse.y) * mouseEase;

      for (let y = 0; y < state.height; y += 1) {
        for (let x = 0; x < state.width; x += 1) {
          const cssX = x * invRatio;
          const cssY = y * invRatio;
          const dx = cssX - mouse.x;
          const dy = cssY - mouse.y;
          const distance = Math.sqrt(dx * dx + dy * dy);
          const influence = mouse.active && mouseArea > 0 ? smoothstep(1 - distance / mouseArea) : 0;
          const fluid = organicFluidValue(simplex, cssX, cssY, time, scale, {
            x: mouse.x,
            y: mouse.y,
            area: mouseArea,
            active: mouse.active,
            influence,
          });
          const atmosphere = organicFluidValue(simplex, cssX + 93.5, cssY - 47.2, time * 0.72 + 19.1, scale * 1.36, {
            x: mouse.x,
            y: mouse.y,
            area: mouseArea,
            active: mouse.active,
            influence: influence * 0.55,
          });
          let color = mix(color1, color2, fluid * 0.78 + atmosphere * 0.22);
          color = mix(bg, color, opacity);

          color = mix(color, hover, influence * 0.18);

          const index = (y * state.width + x) * 4;
          data[index] = color.r;
          data[index + 1] = color.g;
          data[index + 2] = color.b;
          data[index + 3] = 255;
        }
      }

      ctx.putImageData(state.image, 0, 0);
      scheduleDraw();
    }

    window.addEventListener('resize', resize);
    window.addEventListener('mousemove', (event) => {
      mouse.tx = event.clientX;
      mouse.ty = event.clientY;
      mouse.active = true;
    });
    window.addEventListener('mouseleave', () => {
      mouse.active = false;
    });
    window.addEventListener('touchstart', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      mouse.tx = touch.clientX;
      mouse.ty = touch.clientY;
      mouse.active = true;
    }, { passive: true });
    window.addEventListener('touchmove', (event) => {
      const touch = event.touches[0];
      if (!touch) return;
      mouse.tx = touch.clientX;
      mouse.ty = touch.clientY;
    }, { passive: true });
    window.addEventListener('touchend', () => {
      mouse.active = false;
    });
    const classObserver = new MutationObserver(() => {
      if (document.body.classList.contains('global-background-mode-noise')) {
        start();
        return;
      }

      stop();
    });
    classObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        stop();
        return;
      }

      start();
    });
    window.addEventListener('pagehide', () => {
      stop();
      classObserver.disconnect();
    });
    window.addEventListener('pageshow', start);

    resize();
    start();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initGlobalBackgroundNoise, { once: true });
  } else {
    initGlobalBackgroundNoise();
  }

  document.addEventListener('shopify:section:load', initGlobalBackgroundNoise);
})();
